import React from 'react';
import {
  Box,
  Container,
  Typography,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
} from '@mui/material';
import { Person } from '@mui/icons-material';
import { motion } from 'framer-motion';

// Create motion wrapper components
const MotionBox = motion(Box);
const MotionCard = motion(Card);

export const About: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const teamMembers = [
    {
      name: 'Team Member 1',
      role: 'Lead Developer',
      image: '/images/team/member1.jpg',
    },
    {
      name: 'Team Member 2',
      role: 'Trading Strategist',
      image: '/images/team/member2.jpg',
    },
    {
      name: 'Team Member 3',
      role: 'Market Analyst',
      image: '/images/team/member3.jpg',
    },
    {
      name: 'Team Member 4',
      role: 'Community Manager',
      image: '/images/team/member4.jpg',
    },
    {
      name: 'Team Member 5',
      role: 'Risk Manager',
      image: '/images/team/member5.jpg',
    },
    {
      name: 'Team Member 6',
      role: 'Support Specialist',
      image: '/images/team/member6.jpg',
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
    },
  };

  return (
    <Box sx={{ minHeight: '100vh', pt: { xs: 8, md: 10 } }}>
      {/* Hero Section */}
      <Box
        sx={{
          position: 'relative',
          minHeight: { xs: '60vh', md: '70vh' },
          background: `linear-gradient(135deg, rgba(10, 14, 20, 0.9) 0%, rgba(255, 215, 0, 0.2) 50%, rgba(184, 149, 10, 0.2) 100%), url('/images/BearsBulls.jpeg') center/cover no-repeat`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          display: 'flex',
          alignItems: 'center',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(135deg, rgba(10, 14, 20, 0.85) 0%, rgba(0, 0, 0, 0.7) 50%, rgba(10, 14, 20, 0.85) 100%)',
            zIndex: 1,
          },
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2, px: { xs: 2, sm: 3 } }}>
          <MotionBox
            initial="hidden"
            animate="visible"
            variants={containerVariants}
            sx={{
              textAlign: 'center',
              py: { xs: 3, sm: 4, md: 8 },
              px: { xs: 1, sm: 2 },
            }}
          >
            <motion.div variants={itemVariants}>
              <Typography
                variant={isMobile ? 'h3' : 'h1'}
                component="h1"
                gutterBottom
                sx={{
                  background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  fontWeight: 800,
                  mb: 3,
                  textShadow: '0 4px 20px rgba(0, 0, 0, 0.8)',
                  filter: 'drop-shadow(0 2px 10px rgba(255, 215, 0, 0.3))',
                }}
              >
                About T1 FX Team
              </Typography>
            </motion.div>
            
            <motion.div variants={itemVariants}>
              <Typography
                variant={isMobile ? 'h6' : 'h4'}
                component="h2"
                sx={{ 
                  mb: 4, 
                  maxWidth: '800px', 
                  mx: 'auto',
                  color: '#FFFFFF',
                  textShadow: '0 4px 20px rgba(0, 0, 0, 0.9), 0 2px 10px rgba(0, 0, 0, 0.8)',
                  fontWeight: 600,
                  fontSize: { xs: '1.1rem', sm: '1.3rem', md: '1.5rem' },
                  lineHeight: 1.4,
                }}
              >
                Your partner in forex trading solutions, driving innovation in algorithmic trading since our founding.
              </Typography>
            </motion.div>
          </MotionBox>
        </Container>
      </Box>

      {/* Mission & Vision Section */}
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 10 } }}>
        <MotionBox
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={containerVariants}
        >
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', md: 'row' },
            gap: 4 
          }}>
            <Box sx={{ flex: 1 }}>
              <motion.div variants={itemVariants}>
                <Typography
                  variant="h3"
                  component="h2"
                  gutterBottom
                  sx={{
                    color: theme.palette.primary.main,
                    fontWeight: 700,
                    mb: 3,
                  }}
                >
                  Our Mission
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: 'text.primary',
                    fontSize: '1.1rem',
                    lineHeight: 1.8,
                    mb: 4,
                  }}
                >
                  To democratize forex trading by providing cutting-edge Expert Advisors, 
                  comprehensive education, and a supportive community that empowers traders 
                  at every level to achieve consistent profitability.
                </Typography>
              </motion.div>
            </Box>
            
            <Box sx={{ flex: 1 }}>
              <motion.div variants={itemVariants}>
                <Typography
                  variant="h3"
                  component="h2"
                  gutterBottom
                  sx={{
                    color: theme.palette.primary.main,
                    fontWeight: 700,
                    mb: 3,
                  }}
                >
                  Our Vision
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: 'text.primary',
                    fontSize: '1.1rem',
                    lineHeight: 1.8,
                    mb: 4,
                  }}
                >
                  To be the leading platform where technology meets trading expertise, 
                  creating a future where sophisticated trading strategies are accessible 
                  to everyone, regardless of their experience level.
                </Typography>
              </motion.div>
            </Box>
          </Box>
        </MotionBox>
      </Container>

      {/* Team Section - Hidden for now */}
      <Box 
        sx={{ 
          display: 'none', // Hide the team section
          position: 'relative',
          backgroundColor: 'rgba(10, 14, 20, 0.95)', 
          py: { xs: 6, sm: 8, md: 12 },
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `
              radial-gradient(circle at 20% 50%, rgba(255, 215, 0, 0.1) 0%, transparent 50%),
              radial-gradient(circle at 80% 20%, rgba(255, 215, 0, 0.05) 0%, transparent 50%),
              radial-gradient(circle at 40% 80%, rgba(255, 215, 0, 0.08) 0%, transparent 50%),
              linear-gradient(135deg, rgba(10, 14, 20, 0.9) 0%, rgba(20, 25, 35, 0.8) 100%)
            `,
            zIndex: 1,
          },
        }}
      >
        {/* Floating Abstract Shapes */}
        <Box
          sx={{
            position: 'absolute',
            top: { xs: '5%', md: '10%' },
            left: { xs: '2%', md: '5%' },
            width: { xs: '60px', sm: '80px', md: '100px' },
            height: { xs: '60px', sm: '80px', md: '100px' },
            background: 'linear-gradient(45deg, rgba(255, 215, 0, 0.2), rgba(255, 215, 0, 0.05))',
            borderRadius: '30% 70% 70% 30% / 30% 30% 70% 70%',
            animation: 'float 6s ease-in-out infinite',
            zIndex: 1,
            display: { xs: 'none', sm: 'block' },
            '@keyframes float': {
              '0%, 100%': { transform: 'translateY(0px) rotate(0deg)' },
              '50%': { transform: 'translateY(-20px) rotate(180deg)' },
            },
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            top: { xs: '70%', md: '60%' },
            right: { xs: '3%', md: '8%' },
            width: { xs: '50px', sm: '65px', md: '80px' },
            height: { xs: '50px', sm: '65px', md: '80px' },
            background: 'linear-gradient(135deg, rgba(255, 215, 0, 0.15), rgba(255, 215, 0, 0.03))',
            borderRadius: '60% 40% 30% 70% / 60% 30% 70% 40%',
            animation: 'float-reverse 8s ease-in-out infinite',
            zIndex: 1,
            display: { xs: 'none', sm: 'block' },
            '@keyframes float-reverse': {
              '0%, 100%': { transform: 'translateY(0px) rotate(360deg)' },
              '50%': { transform: 'translateY(15px) rotate(180deg)' },
            },
          }}
        />
        
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2, px: { xs: 2, sm: 3 } }}>
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={containerVariants}
          >
            <motion.div variants={itemVariants}>
              <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 8 } }}>
                <Typography
                  variant={isMobile ? 'h4' : 'h2'}
                  component="h2"
                  gutterBottom
                  sx={{ 
                    mb: 2,
                    background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.light}, #FFFFFF)`,
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    fontWeight: 800,
                    position: 'relative',
                    fontSize: { xs: '1.8rem', sm: '2.5rem', md: '3.5rem' },
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      bottom: '-10px',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      width: { xs: '60px', md: '80px' },
                      height: '3px',
                      background: `linear-gradient(90deg, transparent, ${theme.palette.primary.main}, transparent)`,
                      borderRadius: '2px',
                    },
                  }}
                >
                  Meet Our Team
                </Typography>
                <Typography
                  variant={isMobile ? 'body1' : 'h6'}
                  color="rgba(255, 255, 255, 0.8)"
                  sx={{ 
                    mb: 6, 
                    maxWidth: { xs: '90%', md: '600px' }, 
                    mx: 'auto',
                    fontWeight: 400,
                    lineHeight: 1.6,
                    fontSize: { xs: '0.95rem', sm: '1.1rem', md: '1.25rem' },
                    px: { xs: 1, sm: 0 },
                  }}
                >
                  Innovative minds driving the future of algorithmic trading
                </Typography>
              </Box>
            </motion.div>

            <Box sx={{ 
              display: 'flex',
              flexWrap: 'wrap',
              gap: { xs: 3, sm: 4 },
              justifyContent: 'center'
            }}>
              {teamMembers.map((member, index) => (
                <Box 
                  key={index} 
                  sx={{ 
                    width: { xs: '100%', sm: 'calc(50% - 16px)', lg: 'calc(33.333% - 21px)' },
                    maxWidth: '400px'
                  }}
                >
                  <MotionCard
                    variants={itemVariants}
                    whileHover={isMobile ? {} : { 
                      y: -12,
                      rotateY: 5,
                      scale: 1.02,
                    }}
                    whileTap={isMobile ? { scale: 0.98 } : {}}
                    transition={{ 
                      type: "spring", 
                      stiffness: 300, 
                      damping: 20 
                    }}
                    sx={{
                      height: '100%',
                      background: 'rgba(255, 255, 255, 0.05)',
                      backdropFilter: { xs: 'blur(10px)', md: 'blur(20px)' },
                      border: '1px solid rgba(255, 215, 0, 0.2)',
                      borderRadius: { xs: '16px', md: '20px' },
                      overflow: 'hidden',
                      position: 'relative',
                      cursor: 'pointer',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: `linear-gradient(135deg, 
                          rgba(255, 215, 0, 0.1) 0%, 
                          rgba(255, 215, 0, 0.05) 50%, 
                          rgba(255, 215, 0, 0.02) 100%
                        )`,
                        zIndex: 1,
                        transition: 'opacity 0.3s ease',
                      },
                      '&:hover::before': {
                        background: `linear-gradient(135deg, 
                          rgba(255, 215, 0, 0.2) 0%, 
                          rgba(255, 215, 0, 0.1) 50%, 
                          rgba(255, 215, 0, 0.05) 100%
                        )`,
                      },
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        top: '-50%',
                        right: '-50%',
                        width: { xs: '60px', md: '100px' },
                        height: { xs: '60px', md: '100px' },
                        background: `conic-gradient(from 0deg, transparent, rgba(255, 215, 0, 0.4), transparent)`,
                        borderRadius: '50%',
                        animation: isMobile ? 'none' : 'rotate 10s linear infinite',
                        zIndex: 1,
                        display: { xs: 'none', sm: 'block' },
                        '@keyframes rotate': {
                          from: { transform: 'rotate(0deg)' },
                          to: { transform: 'rotate(360deg)' },
                        },
                      },
                    }}
                  >
                    <CardContent sx={{ 
                      p: { xs: 3, md: 4 }, 
                      position: 'relative', 
                      zIndex: 2, 
                      textAlign: 'center' 
                    }}>
                      {/* Abstract geometric frame */}
                      <Box
                        sx={{
                          position: 'relative',
                          width: { xs: 100, sm: 120, md: 140 },
                          height: { xs: 100, sm: 120, md: 140 },
                          mx: 'auto',
                          mb: { xs: 2, md: 3 },
                          '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: { xs: '-6px', md: '-10px' },
                            left: { xs: '-6px', md: '-10px' },
                            right: { xs: '-6px', md: '-10px' },
                            bottom: { xs: '-6px', md: '-10px' },
                            background: `conic-gradient(from 45deg, 
                              ${theme.palette.primary.main}, 
                              transparent, 
                              ${theme.palette.primary.light}, 
                              transparent, 
                              ${theme.palette.primary.main}
                            )`,
                            borderRadius: '50%',
                            zIndex: 1,
                            animation: isMobile ? 'none' : 'rotate-slow 20s linear infinite',
                            '@keyframes rotate-slow': {
                              from: { transform: 'rotate(0deg)' },
                              to: { transform: 'rotate(360deg)' },
                            },
                          },
                        }}
                      >
                        <Box
                          sx={{
                            width: '100%',
                            height: '100%',
                            borderRadius: '50%',
                            background: 'linear-gradient(135deg, rgba(20, 25, 35, 0.9), rgba(10, 14, 20, 0.9))',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            overflow: 'hidden',
                            position: 'relative',
                            zIndex: 2,
                            border: { xs: '2px solid rgba(255, 215, 0, 0.3)', md: '2px solid rgba(255, 215, 0, 0.3)' },
                          }}
                        >
                          <Box
                            component="img"
                            src={member.image}
                            alt={member.name}
                            sx={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                              display: 'none',
                            }}
                            onLoad={(e) => {
                              const target = e.target as HTMLElement;
                              target.style.display = 'block';
                              const sibling = target.nextElementSibling as HTMLElement;
                              if (sibling) {
                                sibling.style.display = 'none';
                              }
                            }}
                            onError={(e) => {
                              const target = e.target as HTMLElement;
                              target.style.display = 'none';
                              const sibling = target.nextElementSibling as HTMLElement;
                              if (sibling) {
                                sibling.style.display = 'flex';
                              }
                            }}
                          />
                          <Person
                            sx={{
                              fontSize: { xs: '2.5rem', sm: '3rem', md: '3.5rem' },
                              color: 'primary.main',
                              display: 'flex',
                              filter: 'drop-shadow(0 4px 8px rgba(255, 215, 0, 0.3))',
                            }}
                          />
                        </Box>
                      </Box>
                      
                      <Typography 
                        variant={isMobile ? 'h6' : 'h5'}
                        component="h3" 
                        gutterBottom
                        sx={{ 
                          fontWeight: 700,
                          color: '#FFFFFF',
                          mb: 1,
                          textShadow: '0 2px 10px rgba(0, 0, 0, 0.5)',
                          fontSize: { xs: '1.1rem', sm: '1.25rem', md: '1.5rem' },
                        }}
                      >
                        {member.name}
                      </Typography>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: 'primary.main',
                          fontWeight: 600,
                          fontSize: { xs: '0.85rem', sm: '0.95rem', md: '1rem' },
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          textShadow: '0 1px 5px rgba(255, 215, 0, 0.3)',
                        }}
                      >
                        {member.role}
                      </Typography>
                    </CardContent>
                  </MotionCard>
                </Box>
              ))}
            </Box>
          </motion.div>
        </Container>
      </Box>
    </Box>
  );
}; 